/**
 * Mobile Install Button Component
 * PWA installation prompt for mobile devices
 */
import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { useTranslation } from 'react-i18next';

const MobileInstallButton = () => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    // Check device type
    const ua = navigator.userAgent;
    const mobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    const android = /Android/i.test(ua);
    const ios = /iPhone|iPad|iPod/i.test(ua);
    
    setIsMobile(mobile);
    setIsAndroid(android);
    setIsIOS(ios);

    // Check if already installed
    const installed = window.matchMedia('(display-mode: standalone)').matches || 
                     window.navigator.standalone ||
                     localStorage.getItem('pwaInstalled') === 'true';
    setIsInstalled(installed);

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Native install prompt available
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        setIsInstalled(true);
        localStorage.setItem('pwaInstalled', 'true');
      }
    } else {
      // Show manual instructions
      setShowInstructions(true);
    }
  };

  const closeInstructions = () => {
    setShowInstructions(false);
  };

  // Don't show if already installed or not mobile
  if (isInstalled || !isMobile) {
    return null;
  }

  return (
    <>
      <Button 
        variant="outline" 
        size="sm"
        onClick={handleInstall}
        className="flex-1 text-xs bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
      >
        📲 {t('pwa.install', 'Installieren')}
      </Button>

      {/* Manual Installation Instructions Modal */}
      {showInstructions && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">
                📲 {t('pwa.installTitle', 'App installieren')}
              </h3>
              <button 
                onClick={closeInstructions}
                className="text-gray-400 hover:text-gray-600 text-xl"
              >
                ×
              </button>
            </div>

            {isAndroid && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  {t('pwa.androidInstructions', 'So installieren Sie Babelmix auf Ihrem Android-Gerät:')}
                </p>
                <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
                  <li>{t('pwa.android1', 'Tippen Sie auf die drei Punkte (⋮) oben rechts im Browser')}</li>
                  <li>{t('pwa.android2', 'Wählen Sie "Zum Startbildschirm hinzufügen" oder "App installieren"')}</li>
                  <li>{t('pwa.android3', 'Bestätigen Sie die Installation')}</li>
                </ol>
                <div className="mt-4 p-3 bg-emerald-50 rounded-lg text-sm text-emerald-700">
                  💡 {t('pwa.androidTip', 'Nach der Installation finden Sie die App auf Ihrem Startbildschirm.')}
                </div>
              </div>
            )}

            {isIOS && (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  {t('pwa.iosInstructions', 'So installieren Sie Babelmix auf Ihrem iPhone/iPad:')}
                </p>
                <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
                  <li>{t('pwa.ios1', 'Tippen Sie auf das Teilen-Symbol (□↑) unten im Safari')}</li>
                  <li>{t('pwa.ios2', 'Scrollen Sie und wählen Sie "Zum Home-Bildschirm"')}</li>
                  <li>{t('pwa.ios3', 'Tippen Sie auf "Hinzufügen"')}</li>
                </ol>
                <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                  ⚠️ {t('pwa.iosTip', 'Funktioniert nur in Safari, nicht in Chrome oder anderen Browsern.')}
                </div>
              </div>
            )}

            <Button 
              onClick={closeInstructions}
              className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700"
            >
              {t('common.close', 'Verstanden')}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileInstallButton;
